import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import slugify from 'slugify';
import { Updater } from 'use-immer';
import { DICTIONARY } from 'shared/constants';
import useCanEditField from 'shared/hooks/useCanEditField';
import AlertContext from 'shared/contexts/AlertContext';
import CardBase from './CardBase';
import IconRow from './IconRow';
import IconRowCell from './IconRowCell';
import ToggleableForm from './forms/ToggleableForm';
import CustomSnackbar from './CustomSnackbar';
import CardHeader from './CardHeader';

interface StyledCardBaseProps {
  isNarrow?: boolean;
  isFormViewStyle?: boolean;
}

const StyledCardBase = styled(CardBase, {
  shouldForwardProp: (prop) => !['isNarrow', 'isFormViewStyle'].includes(prop as string),
})<StyledCardBaseProps>(({ theme, isNarrow, isFormViewStyle }) => {
  if (isFormViewStyle) {
    return {
      borderRadius: 0,
      boxShadow: 'none',
      margin: 0,
      padding: 0,
    };
  }

  return {
    padding: '1rem',
    marginBottom: '1rem',
    [theme.breakpoints.up('xl')]: {
      padding: isNarrow ? '1rem' : '2rem',
    },
  };
});

/**
 *
 * FormCard can be used for either as an editable or static display of
 * information.
 *
 * FormCard expects the following props:
 * source — Any object
 * fields — A configuration array of ToggleableField values that says which
 *          properties of source to show and how
 * labelGetter — A function to translate property names to display names, ie.
 *               phoneNumber => phone number
 * canEditForm — Whether the form can be edited; if false, card will be static
 * title — Title of card
 *
 * Some optional layout options:
 * actionsAtTop - Save/Cancel buttons at top of form (defaults to true)
 * actionsAtBottom - Save/Cancel buttons at bottom of form (defaults to false)
 *    Notes: - "Edit" button will always appear at top.
 *           - It's possible for actions to be at both top and bottom
 * autoEdit - Start in an editable state (if allowed)
 * customSaveEdits - custom handler function when click on save button
 * narrow — Makes card a single column and condenses padding.
 * useCappedLabels — Caps the label of each field
 * renderAsList — Displays a bullet next to each value
 * useIcon — Leaves space for an icon next to each field
 * icon — An icon to use next to the title
 * isFormView - display without Cardbase styles

 * If the card is being used as an editable form, it expects some more
   properties:
 * setSource: An Immer setter that updates source with each edit.
 * resetEdits: Fires on cancel
 * saveEdits: Fires on save
 */

interface Props {
  actionsAtBottom?: boolean;
  actionsAtTop?: boolean;
  autoEdit?: boolean;
  canEditForm?: boolean;
  className?: string;
  fields: Array<ToggleableField>;
  icon?: Icon;
  isFormView?: boolean;
  labelGetter: (x: string, y?: string) => string;
  narrow?: boolean;
  noHeaderIcon?: boolean;
  onCancelCallback?: () => void;
  onSaveCallback?: () => void;
  renderAsList?: boolean;
  customSaveEdits?: (fieldsData: EditableFieldsData) => Promise<void>;
  resetEdits?: () => void;
  saveEdits?: () => Promise<void>;
  setSource?: Updater<any>;
  singleColumn?: boolean;
  source: any;
  title: string;
  type: ProfileType;
  useCappedLabels?: boolean;
  useIcon?: boolean;
  isIndividualSponsored?: boolean;
  route?: string
}

const FormCard: React.FC<Props> = ({
  actionsAtBottom = false,
  actionsAtTop = true,
  autoEdit,
  canEditForm,
  className,
  fields,
  icon,
  isFormView,
  customSaveEdits,
  labelGetter,
  narrow,
  noHeaderIcon,
  onCancelCallback,
  onSaveCallback,
  renderAsList,
  resetEdits,
  saveEdits,
  setSource,
  singleColumn,
  source,
  title,
  type,
  useCappedLabels,
  useIcon,
  isIndividualSponsored,
  route,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [alert, setAlert] = useState<Snack | null>(null);
  const [reset, setReset] = useState<number | null>(null);

  // Reset to default state if field permissions change
  const canEdit = useCanEditField();
  const canEditCount = fields.filter((field) => canEdit(field, type)).length;
  if (reset !== canEditCount) {
    setReset(canEditCount);
  }

  const [isEditing, setIsEditing] = useState(
    (canEditCount > 0 && autoEdit) || false,
  );

  const handleCancel = () => {
    setIsEditing(false);
    if (resetEdits) {
      resetEdits();
    }
    if (onCancelCallback) {
      onCancelCallback();
    }
  };

  const handleEdit = () => setIsEditing(true);

  const handleSave = async () => {
    if (saveEdits) {
      setIsLoading(true);
      setHasAttemptedSave(true);
      try {
        if (customSaveEdits) {
          const editableFields = fields as Array<ToggleableFieldBase>;
          const fieldsData = editableFields.reduce<EditableFieldsData>((acc, item) => ({
            ...acc,
            [item.fieldName]: item.value === undefined || item.value === null ? null : item.value,
          }), {});
          await customSaveEdits(fieldsData);
        } else {
          await saveEdits();
        }
        setIsEditing(false);
        setIsLoading(false);
        if (onSaveCallback) {
          onSaveCallback();
        }
      } catch (err: any) {
        setAlert([
          err.message || DICTIONARY.SaveError,
          'error',
        ]);
        setIsLoading(false);
      }
    }
  };

  const EditActions = () => (
    <IconRowCell type="actions">
      <Button
        id={slugify(`${title} save`)}
        sx={{ marginLeft: '0.5rem' }}
        color="primary"
        data-function="save"
        disabled={isLoading}
        onClick={handleSave}
      >
        Save
      </Button>
      <Button
        id={slugify(`${title} cancel`)}
        sx={{ marginLeft: '0.5rem' }}
        onClick={handleCancel}
        color="secondary"
        disabled={isLoading}
        data-function="cancel"
      >
        Cancel
      </Button>
    </IconRowCell>
  );

  useEffect(() => {
    setIsEditing(
      (canEditCount > 0 && autoEdit) || false,
    );
    if (resetEdits) {
      resetEdits();
    }
  }, [reset]);

  return (
    <StyledCardBase
      className={className}
      isNarrow={narrow}
      isFormViewStyle={isFormView}
      backgroundColor={isFormView ? 'transparent' : undefined}
      component="section"
    >
      <CardHeader
        actionsAtTop={actionsAtTop}
        canEditForm={canEditForm}
        editActions={<EditActions />}
        handleEdit={handleEdit}
        icon={icon}
        isEditing={isEditing}
        noHeaderIcon={noHeaderIcon}
        resetEdits={resetEdits}
        saveEdits={saveEdits}
        title={title}
      />

      {isLoading ? (
        <CircularProgress />
      ) : (
        <ToggleableForm
          fields={fields}
          isEditing={isEditing}
          labelGetter={labelGetter}
          renderAsList={renderAsList}
          narrow={narrow}
          singleColumn={singleColumn}
          useCappedLabels={useCappedLabels}
          useIcon={useIcon}
          source={source}
          type={type}
          isIndividualSponsored={isIndividualSponsored}
          setSource={setSource}
          hasAttemptedSave={hasAttemptedSave}
          route={route}
        />
      )}

      {isEditing && actionsAtBottom && (
        <IconRow sx={{ marginTop: '1rem' }}>
          <EditActions />
        </IconRow>
      )}

      {/*
      The alert context set in App.tsx was causing re-renders and
      resetting the `isEditing` value in forms. We override that context
      with one specific to FormcCard to avoid that problem.
      */}
      <AlertContext.Provider value={[alert, setAlert]}>
        <CustomSnackbar />
      </AlertContext.Provider>
    </StyledCardBase>
  );
};

export default React.memo(FormCard);
